import aiShine from 'assets/images/png/aiShine.png';
import dbLogoV2 from 'assets/images/png/dbLogoV2.png';
import { ReactComponent as IconSend } from 'assets/images/svg/graySendIcon.svg';
import React, { useState, useEffect, useRef } from 'react';

import './Chat.scss';
import AgentExecution from './agentExecution/AgentExecution';
import Message from './message/Message';
import { ChatMessage, useMessagesContext } from './MessagesContext';

const Chat = (): React.ReactElement => {
  const { messages, addMessage, agentExecuting } = useMessagesContext();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null); // Ref for tracking the chat end
  const scrollToBottom = () => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom(); // Automatically scroll to the bottom when messages change
  }, [messages]);

  const handleSendMessage = () => {
    if (input.trim()) {
      const userInput = input.trim();
      setInput(''); // Clear the input field

      // Add user's message
      addMessage({
        id: Date.now(),
        text: userInput,
        isUser: true,
      });

      // Add AgentExecution to handle agent's response
      addMessage({
        id: Date.now() + 1,
        isUser: false,
        agentExecution: true,
        userInput,
      });
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (agentExecuting) return;
    if (e.key === 'Enter') handleSendMessage();
  };

  const renderMessage = (message: ChatMessage, index: number) => (
    <div
      key={message.id + index}
      data-testid={`${message.isUser ? `chat-message-user-${index}` : `chat-message-bot-${index}`}`}
      className={`${message.isUser ? 'chat__message--user' : 'chat__message--bot'}`}
    >
      {!message.isUser && <img src={dbLogoV2} className='db-logo' alt='DB Logo' loading='lazy' />}
      {message.agentExecution ? (
        <AgentExecution
          key={message.id + index}
          userInput={message.userInput ?? ''}
          isUser={false}
          onContentUpdate={scrollToBottom}
          messageId={message.id}
        />
      ) : (
        <Message key={message.id + index} text={message.text || ''} isUser={message.isUser} />
      )}
    </div>
  );

  return (
    <div data-testid={'chat-container'} className={'chat-container'}>
      <div className={`chat ${messages.length === 0 ? 'chat--centered' : ''}`}>
        <div></div> {/* Empty div for alignment purpose remove later and fix */}
        {messages.length === 0 && (
          <span className='chat__hero'>
            <h2 data-testid='chat-hero-title' className='chat__hero--title'>
              What can I help with?
            </h2>
            <p data-testid='chat-hero-description' className='chat__hero--description'>
              Ask me anything about Demandbase, like how to create a new journey, export data, or
              create a new account list.
            </p>
          </span>
        )}
        <div className={`chat__messages ${messages.length === 0 ? 'chat__messages--empty' : ''}`}>
          {messages.map((message, index) => {
            return renderMessage(message, index);
          })}
          {/* The ref to scroll to */}
          <div ref={messagesEndRef} />
        </div>
        <div className='chat__input'>
          <img src={aiShine} alt='AI Shine Icon' className='input-icon' loading='lazy' />
          <input
            data-testid={'chat-box'}
            type='text'
            placeholder='Ask Demandbase bot a question...'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          <button data-testid={'chat-box-send-btn'} onClick={handleSendMessage} disabled={!input}>
            <IconSend />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;
